import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Input,
  Descriptions,
  Divider,
  Switch,
  Table,
  Button,
  Space,
  Tag,
  Alert,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ExportOutlined,
  EditOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import TicketModelPopup from "../../../../components/modelpopup/TicketModelPopup";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import { Tooltip } from "react-tooltip";
import {
  FaPlus,
  FaMinus,
  FaFileExport,
  FaEdit,
  FaCheck,
  FaTimes,
} from "react-icons/fa";

import { useSelector } from "react-redux";
import {
  formatNumbers,
  getDate,
  sumArrayProperties,
} from "../../../../utils/openformat/numbers";
import { useTranslation } from "react-i18next";

import DeleteModal from "../../../../components/modelpopup/deletePopup";
import { Link } from "react-router-dom";
import BbFilter from "../../../../components/BbFilter";
export default function BbBeforePost() {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [statistics, setStatistics] = useState({
    total: 0,
    audited: 0,
    pending: 0,
    totalAmount: 0,
  });

  // Load and process data
  const loadData = useCallback(() => {
    setLoading(true);
    setError(null);

    try {
      const raw = localStorage.getItem("expensesData");
      if (!raw) {
        setError("لا توجد بيانات. يرجى معالجة المستندات أولاً.");
        setData([]);
        return;
      }

      const parsed = JSON.parse(raw);
      const enriched = Array.isArray(parsed) ? parsed : [parsed];

      const processedData = enriched.map((entry, index) => ({
        key: index,
        id: `doc_${index}`,
        audited: entry.audited || false,
        ...entry,
      }));

      setData(processedData);

      // Calculate statistics
      const stats = processedData.reduce(
        (acc, item) => {
          acc.total += 1;
          acc.audited += item.audited ? 1 : 0;
          acc.pending += !item.audited ? 1 : 0;

          const amount = parseFloat(
            item.invoice_receipt_details?.total_due || 0
          );
          acc.totalAmount += isNaN(amount) ? 0 : amount;

          return acc;
        },
        { total: 0, audited: 0, pending: 0, totalAmount: 0 }
      );

      setStatistics(stats);
    } catch (e) {
      console.error("Failed to parse expensesData:", e);
      setError("فشل في تحميل بيانات المستندات. يرجى التحقق من تنسيق البيانات.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Update document audit status
  const updateDocumentAudit = useCallback((key, audited) => {
    setData((prevData) => {
      const newData = prevData.map((item) =>
        item.key === key ? { ...item, audited } : item
      );

      // Update localStorage
      localStorage.setItem("expensesData", JSON.stringify(newData));

      // Recalculate statistics
      const stats = newData.reduce(
        (acc, item) => {
          acc.total += 1;
          acc.audited += item.audited ? 1 : 0;
          acc.pending += !item.audited ? 1 : 0;

          const amount = parseFloat(
            item.invoice_receipt_details?.total_due || 0
          );
          acc.totalAmount += isNaN(amount) ? 0 : amount;

          return acc;
        },
        { total: 0, audited: 0, pending: 0, totalAmount: 0 }
      );

      setStatistics(stats);

      return newData;
    });
  }, []);

  // Bulk audit operations
  const handleBulkAudit = useCallback(
    (audit = true) => {
      selectedRows.forEach((key) => {
        updateDocumentAudit(key, audit);
      });
      message.success(
        `تم ${audit ? "تدقيق" : "إلغاء تدقيق"} ${selectedRows.length} مستند`
      );
      setSelectedRows([]);
    },
    [selectedRows, updateDocumentAudit]
  );

  // Export functionality
  const handleExport = useCallback(() => {
    const exportData = data.filter((item) => item.audited);

    if (exportData.length === 0) {
      message.warning("لا توجد مستندات مدققة للتصدير");
      return;
    }

    const exportJson = {
      export_date: new Date().toISOString(),
      company: "Fintrack Demo",
      period: "8-2025",
      total_documents: exportData.length,
      total_amount: statistics.totalAmount,
      documents: exportData,
    };

    const blob = new Blob([JSON.stringify(exportJson, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `bookbot_export_${
      new Date().toISOString().split("T")[0]
    }.json`;
    link.click();
    URL.revokeObjectURL(url);

    message.success("تم تصدير البيانات بنجاح");
  }, [data, statistics]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Enhanced table columns
  const columns = useMemo(
    () => [
      {
        title: "حالة المستند",
        key: "status",
        width: 120,
        render: (_, record) => (
          <Space direction="vertical" size="small">
            {record.audited ? (
              <Tag color="success" icon={<CheckCircleOutlined />}>
                مدقق
              </Tag>
            ) : (
              <Tag color="warning">قيد المراجعة</Tag>
            )}
          </Space>
        ),
      },
      {
        title: "اسم المستند",
        dataIndex: "document_name",
        key: "document_name",
        ellipsis: true,
        render: (text) =>
          text || <span style={{ color: "#ccc" }}>غير محدد</span>,
      },
      {
        title: "نوع المستند",
        dataIndex: ["invoice_receipt_details", "document_type"],
        key: "document_type",
        filters: [
          { text: "فاتورة", value: "فاتورة" },
          { text: "إيصال", value: "إيصال" },
          { text: "سند قبض", value: "سند قبض" },
        ],
        onFilter: (value, record) =>
          record.invoice_receipt_details?.document_type?.includes(value),
      },
      {
        title: "رقم المستند",
        dataIndex: ["invoice_receipt_details", "invoice_number"],
        key: "invoice_number",
        sorter: (a, b) => {
          const aNum = a.invoice_receipt_details?.invoice_number || "";
          const bNum = b.invoice_receipt_details?.invoice_number || "";
          return aNum.localeCompare(bNum);
        },
      },
      {
        title: "تاريخ المستند",
        dataIndex: ["invoice_receipt_details", "date"],
        key: "date",
        sorter: (a, b) => {
          const aDate = new Date(a.invoice_receipt_details?.date || 0);
          const bDate = new Date(b.invoice_receipt_details?.date || 0);
          return aDate - bDate;
        },
        render: (date) =>
          date ? new Date(date).toLocaleDateString("ar-SA") : "-",
      },
      {
        title: "المورد",
        dataIndex: ["invoice_receipt_details", "supplier"],
        key: "supplier",
        ellipsis: true,
        filters: [
          ...new Set(
            data
              .map((item) => item.invoice_receipt_details?.supplier)
              .filter(Boolean)
          ),
        ].map((supplier) => ({
          text: supplier,
          value: supplier,
        })),
        onFilter: (value, record) =>
          record.invoice_receipt_details?.supplier === value,
      },
      {
        title: "المبلغ المستحق",
        dataIndex: ["invoice_receipt_details", "total_due"],
        key: "total_due",
        sorter: (a, b) => {
          const aAmount = parseFloat(a.invoice_receipt_details?.total_due || 0);
          const bAmount = parseFloat(b.invoice_receipt_details?.total_due || 0);
          return aAmount - bAmount;
        },
        render: (amount) => (amount ? formatNumbers(amount) : "-"),
      },
      {
        title: "الإجراءات",
        key: "actions",
        width: 150,
        render: (_, record) => (
          <Space>
            <Switch
              size="small"
              checked={record.audited}
              onChange={(checked) => updateDocumentAudit(record.key, checked)}
              checkedChildren={<FaCheck />}
              unCheckedChildren={<FaTimes />}
              className="btn-switch"
            />
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              title="عرض التفاصيل"
            />
          </Space>
        ),
      },
    ],
    [data, updateDocumentAudit]
  );

  // Row selection configuration
  const rowSelection = {
    selectedRowKeys: selectedRows,
    onChange: setSelectedRows,
    getCheckboxProps: (record) => ({
      disabled: record.audited, // Disable selection for already audited items
    }),
  };

  const expandedRowRender = (record) => {
    const invoice = record.invoice_receipt_details || {};
    const bank = record.bank_transfer_details || {};

    return (
      <div className="details-table expanded-content animate-expand  p-4 rounded-2">
        <Descriptions title="تفاصيل الفاتورة" bordered size="small" column={2}>
          {Object.entries(invoice)
            .filter(([key]) => key !== "invoice_number" && key !== "date")
            .map(([key, value]) => {
              if (Array.isArray(value)) return null;
              return (
                <Descriptions.Item key={key} label={key}>
                  {value || "-"}
                </Descriptions.Item>
              );
            })}
        </Descriptions>

        {Array.isArray(invoice.items) && invoice.items.length > 0 && (
          <>
            <Divider orientation="left">العناصر</Divider>
            <Table
              dataSource={invoice.items.map((item, i) => ({ ...item, key: i }))}
              columns={[
                { title: "رقم الصنف", dataIndex: "item_number" },
                { title: "الوصف", dataIndex: "description" },
                { title: "الكمية", dataIndex: "quantity" },
                { title: "سعر الوحدة", dataIndex: "unit_price_ils" },
                { title: "الإجمالي", dataIndex: "total_ils" },
              ]}
              pagination={false}
              size="small"
            />
          </>
        )}

        {Array.isArray(invoice.payment_details) &&
          invoice.payment_details.length > 0 && (
            <>
              <Divider orientation="left">تفاصيل الدفع</Divider>
              <Table
                dataSource={invoice.payment_details.map((p, i) => ({
                  ...p,
                  key: i,
                }))}
                columns={[
                  { title: "التفاصيل", dataIndex: "line_details" },
                  { title: "نوع الدفع", dataIndex: "payment_type" },
                  { title: "تاريخ الدفع", dataIndex: "payment_date" },
                  { title: "المبلغ", dataIndex: "amount_ils" },
                ]}
                pagination={false}
                size="small"
              />
            </>
          )}

        <Divider orientation="left">تفاصيل التحويل البنكي</Divider>
        <Descriptions bordered size="small" column={2}>
          {Object.entries(bank).map(([key, value]) => (
            <Descriptions.Item key={key} label={key}>
              {value || "-"}
            </Descriptions.Item>
          ))}
        </Descriptions>
      </div>
    );
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content container-fluid">
          {/* Page Header */}
          <Breadcrumbs
            maintitle="BookBot - مراجعة المستندات"
            title={t("dashboard")}
            subtitle="مراجعة وتدقيق المستندات"
          />

          {/* Error Alert */}
          {error && (
            <div className="row mb-3">
              <div className="col-12">
                <Alert
                  message="خطأ في تحميل البيانات"
                  description={error}
                  type="error"
                  showIcon
                  closable
                  action={
                    <Button size="small" onClick={loadData}>
                      إعادة المحاولة
                    </Button>
                  }
                />
              </div>
            </div>
          )}

          {/* Statistics Dashboard */}
          <div className="row mb-4">
            <div className="col-xl-3 col-sm-6 col-12">
              <div className="card">
                <div className="card-body">
                  <div className="dash-widget-header">
                    <span className="dash-widget-icon text-primary border-primary">
                      <FaFileExport />
                    </span>
                    <div className="dash-count">
                      <h3>{statistics.total}</h3>
                    </div>
                  </div>
                  <div className="dash-widget-info">
                    <h6 className="text-muted">إجمالي المستندات</h6>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-sm-6 col-12">
              <div className="card">
                <div className="card-body">
                  <div className="dash-widget-header">
                    <span className="dash-widget-icon text-success border-success">
                      <CheckCircleOutlined />
                    </span>
                    <div className="dash-count">
                      <h3>{statistics.audited}</h3>
                    </div>
                  </div>
                  <div className="dash-widget-info">
                    <h6 className="text-muted">مستندات مدققة</h6>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-sm-6 col-12">
              <div className="card">
                <div className="card-body">
                  <div className="dash-widget-header">
                    <span className="dash-widget-icon text-warning border-warning">
                      <ExclamationCircleOutlined />
                    </span>
                    <div className="dash-count">
                      <h3>{statistics.pending}</h3>
                    </div>
                  </div>
                  <div className="dash-widget-info">
                    <h6 className="text-muted">قيد المراجعة</h6>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-sm-6 col-12">
              <div className="card">
                <div className="card-body">
                  <div className="dash-widget-header">
                    <span className="dash-widget-icon text-info border-info">
                      ₪
                    </span>
                    <div className="dash-count">
                      <h3>{formatNumbers(statistics.totalAmount)}</h3>
                    </div>
                  </div>
                  <div className="dash-widget-info">
                    <h6 className="text-muted">إجمالي المبلغ</h6>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Company Information Card */}
          <div className="row mb-4">
            <div className="col-md-12">
              <div className="card bookbot-company-info">
                <div className="card-body">
                  <div className="row">
                    <div className="col-sm-4 m-b-20">
                      <ul className="list-unstyled mb-0">
                        <li>
                          <span className="fw-bold">الشركة:</span> Fintrack Demo
                        </li>
                        <li>
                          <span className="fw-bold">المستخدم:</span> DemoUser
                        </li>
                      </ul>
                    </div>
                    <div className="col-sm-4 m-b-20 text-center">
                      <ul className="list-unstyled mb-0">
                        <li>
                          <h3>BookBot</h3>
                        </li>
                        <li>
                          <span className="fw-bold">قيود شهر:</span> 8-2025
                        </li>
                        <li>
                          <span className="fw-bold">رقم الشركة:</span>{" "}
                          5199999999
                        </li>
                      </ul>
                    </div>
                    <div className="col-sm-4 m-b-20 text-end">
                      <ul className="list-unstyled mb-0">
                        <li>
                          <span className="fw-bold">نوع النموذج:</span> MassAi
                        </li>
                        <li>
                          <span className="fw-bold">تصدير القيد إلى:</span>{" "}
                          <a
                            href="https://mass-billing.web.app/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-white text-decoration-underline"
                          >
                            MassBill
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="row mb-4">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex flex-wrap gap-2">
                    <Button
                      type="primary"
                      icon={<CheckCircleOutlined />}
                      onClick={() => handleBulkAudit(true)}
                      disabled={selectedRows.length === 0}
                    >
                      تدقيق المحدد ({selectedRows.length})
                    </Button>
                    <Button
                      icon={<ExclamationCircleOutlined />}
                      onClick={() => handleBulkAudit(false)}
                      disabled={selectedRows.length === 0}
                    >
                      إلغاء تدقيق المحدد
                    </Button>
                    <Button
                      type="primary"
                      icon={<FaFileExport />}
                      onClick={handleExport}
                      disabled={statistics.audited === 0}
                    >
                      تصدير المستندات المدققة ({statistics.audited})
                    </Button>
                    <Button
                      icon={<FaEdit />}
                      onClick={loadData}
                      loading={loading}
                    >
                      تحديث البيانات
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              <ul className="nav nav-tabs nav-tabs-solid nav-justified bookbot-tabs">
                <li className="nav-item">
                  <Link
                    className="nav-link active"
                    to="#basictab1"
                    data-bs-toggle="tab"
                  >
                    معاينة الملفات
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    className="nav-link"
                    to="#basictab2"
                    data-bs-toggle="tab"
                  >
                    <i className="la la-upload la-lg me-2"></i>
                    تحميل مستندات
                  </Link>
                </li>

                <li className="nav-item">
                  <Link
                    className="nav-link"
                    to="#basictab3"
                    data-bs-toggle="tab"
                  >
                    More...
                  </Link>
                </li>

                {/* <li className="nav-item">
                          <Link
                            className="nav-link"
                            data-bs-toggle="modal"
                            data-bs-target="#ai-agent"
                            // data-bs-toggle="tab"
                          >
                            مساعد AI
                          </Link>
                        </li> */}
              </ul>
            </div>

            {/* {prompt && <AiGemelAgent prompt={prompt} />} */}
          </div>
          <BbFilter />

          <div className="tab-content">
            <div className="tab-pane show active" id="basictab1">
              <div className="card bookbot-enhanced-table">
                <div className="card-header border-0">
                  <h3 className="card-title mb-2 text-center mt-2">
                    مراجعة وتدقيق المستندات
                  </h3>
                </div>
                <div className="card-body">
                  {loading ? (
                    <div className="text-center py-5">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">جاري التحميل...</span>
                      </div>
                      <p className="mt-3 text-muted">جاري تحميل البيانات...</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table
                        columns={columns}
                        dataSource={data}
                        rowSelection={rowSelection}
                        expandable={{
                          expandedRowRender: (record) => (
                            <div className="bookbot-expanded-content">
                              {expandedRowRender(record)}
                            </div>
                          ),
                          expandIcon: ({ expanded, onExpand, record }) =>
                            expanded ? (
                              <>
                                <FaMinus
                                  onClick={(e) => onExpand(record, e)}
                                  data-tooltip-id={`expand-tip-${record.key}`}
                                  data-tooltip-content="إخفاء التفاصيل"
                                  style={{ cursor: "pointer", marginRight: 8 }}
                                  className="ant-table-row-expand-icon border-none"
                                />
                                <Tooltip
                                  id={`expand-tip-${record.key}`}
                                  place="bottom"
                                  style={{ zIndex: "100" }}
                                />
                              </>
                            ) : (
                              <>
                                <FaPlus
                                  onClick={(e) => onExpand(record, e)}
                                  data-tooltip-id={`expand-tip-${record.key}`}
                                  data-tooltip-content="عرض التفاصيل"
                                  className="ant-table-row-expand-icon"
                                  style={{
                                    cursor: "pointer",
                                    marginRight: 8,
                                  }}
                                />
                                <Tooltip
                                  id={`expand-tip-${record.key}`}
                                  place="left"
                                  style={{ zIndex: "100" }}
                                />
                              </>
                            ),
                        }}
                        pagination={{
                          pageSize: 10,
                          showSizeChanger: true,
                          showQuickJumper: true,
                          showTotal: (total, range) =>
                            `${range[0]}-${range[1]} من ${total} مستند`,
                        }}
                        scroll={{ x: 1200 }}
                        size="middle"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="tab-pane" id="basictab2">
              <div className="card">
                <div className="card-header border-bottom-0 mt-2">
                  <h2 className="text-primary card-title text-center pt-2">
                    فحص ملفات ضريبية
                  </h2>
                </div>
                <div className="card-body"></div>
              </div>
            </div>
            <div className="tab-pane" id="basictab3">
              <div className="card">
                <div className="card-header">
                  <h1 className="text-primary  text-center p-4">
                    Wait For MAssAI 2027
                  </h1>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <TicketModelPopup />
      <DeleteModal Name="Delete Ticket" />
    </>
  );
}
